import { useState, useCallback, useRef } from 'react';
import type { QuickReplyAction, QuickReplyOption } from '../../types';
import styles from './ActionCard.module.css';
import MdrConsultationModal from './MdrConsultationModal';

const MDR_CARD_TITLE = 'Strengthen your defenses with Cowbell MDR';
const MDR_REQUEST_OPTION = 'Request Consultation';

interface Props {
  readonly action: QuickReplyAction;
  readonly onSendMessage?: (message: string, skill?: string) => void;
  /** False when this card belongs to a historical assistant message — the
   *  question has been superseded, so the card must render inert even when
   *  local selection state was lost to a remount (OMNI-702). */
  readonly isLastMessage?: boolean;
  /** The user turn that answered this card's message, if any. Presence
   *  marks the question as answered; matching it against the option labels
   *  restores which option was picked after a remount or reload. */
  readonly followupUserText?: string;
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function hasConfirmationLayout(action: QuickReplyAction): boolean {
  if (action.layout === 'confirmation') return true;
  return Boolean(
    action.headline ||
      action.fromLabel ||
      action.toLabel ||
      (action.items && action.items.length > 0)
  );
}

function optionButtonClass(
  isSelected: boolean,
  isFaded: boolean,
  stylesMap: typeof styles
): string {
  if (isSelected) return stylesMap.optionSelected;
  if (isFaded) return stylesMap.optionFaded;
  return stylesMap.optionButton;
}

function confirmationButtonClass(
  isPrimary: boolean,
  isSelected: boolean,
  isFaded: boolean,
  stylesMap: typeof styles
): string {
  let base: string;
  if (isPrimary) {
    base = isSelected
      ? stylesMap.confirmApproveSelected
      : stylesMap.confirmApprove;
  } else {
    base = isSelected
      ? stylesMap.confirmRejectSelected
      : stylesMap.confirmReject;
  }
  if (isFaded) return `${base} ${stylesMap.confirmButtonFaded}`;
  return base;
}

function deriveSkill(option: QuickReplyOption): string | undefined {
  if (option.skill) return option.skill;
  if (option.value && option.value !== option.label) return option.value;
  return undefined;
}

export default function QuickReplyPicker({
  action,
  onSendMessage,
  isLastMessage = true,
  followupUserText,
}: Props) {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pendingOptionRef = useRef<QuickReplyOption | null>(null);
  // Consultation cards are service-aware (MDR, Identity Threat Detection,
  // Pen Testing, ... or a generic specialist card) and all open the same
  // consultation form. The MDR title check keeps older chat-agent cards,
  // which predate `consultationService`, working.
  const isConsultationCard =
    Boolean(action.consultationService) || action.title === MDR_CARD_TITLE;

  const options = action.options ?? [];

  // Local click state does not survive remounts, so the answered state is
  // re-derived from the transcript: a user turn following this card means
  // the question was answered (and its text identifies which option), and
  // a later assistant message means it was superseded (OMNI-702).
  const answeredValue =
    followupUserText === undefined
      ? null
      : options.find((o) => o.label === followupUserText)?.value ?? null;
  const effectiveSelected = selectedValue ?? answeredValue;
  const isAnswered =
    effectiveSelected !== null ||
    !isLastMessage ||
    followupUserText !== undefined;

  const handleSelect = useCallback(
    (option: QuickReplyOption) => {
      if (isAnswered || isModalOpen) return;

      if (isConsultationCard && option.value === MDR_REQUEST_OPTION) {
        pendingOptionRef.current = option;
        setIsModalOpen(true);
        return;
      }

      setSelectedValue(option.value);
      onSendMessage?.(option.label, deriveSkill(option));
    },
    [isAnswered, isModalOpen, onSendMessage, isConsultationCard]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    pendingOptionRef.current = null;
  }, []);

  const handleSubmissionComplete = useCallback(() => {
    const pendingOption = pendingOptionRef.current;
    setIsModalOpen(false);
    pendingOptionRef.current = null;
    if (!pendingOption) return;

    setSelectedValue(pendingOption.value);
    onSendMessage?.(pendingOption.label, deriveSkill(pendingOption));
  }, [onSendMessage]);

  const structured = hasConfirmationLayout(action);
  const showLetters = options.length > 1 && !structured;
  const itemCount = action.itemsCount ?? action.items?.length ?? 0;
  const items = action.items ?? [];
  const itemsOverflow = Number(action.itemsOverflow) || 0;
  const userCountLabel = itemCount === 1 ? '1 user' : `${itemCount} users`;

  return (
    <>
      <div
        className={
          structured ? `${styles.card} ${styles.confirmCard}` : styles.card
        }
      >
        {structured ? (
          <div className={styles.confirmBody}>
            <div className={styles.confirmHeader}>
              <div className={styles.confirmHeadline}>
                {action.headline || 'Confirm'}
              </div>
              {itemCount > 0 && (
                <span className={styles.confirmBadge}>{userCountLabel}</span>
              )}
            </div>

            {(action.fromLabel || action.toLabel) && (
              <div className={styles.confirmRoute}>
                {action.fromLabel && (
                  <span>
                    <strong>From:</strong> {action.fromLabel}
                  </span>
                )}
                {action.fromLabel && action.toLabel && (
                  <span className={styles.confirmRouteArrow} aria-hidden="true">
                    →
                  </span>
                )}
                {action.toLabel && (
                  <span>
                    <strong>To:</strong> {action.toLabel}
                  </span>
                )}
              </div>
            )}

            {items.length > 0 && (
              <div className={styles.confirmUsers}>
                <div className={styles.confirmUsersTitle}>Users to migrate</div>
                <ul className={styles.confirmItemGrid}>
                  {items.map((item) => (
                    <li key={item} className={styles.confirmItem}>
                      {item}
                    </li>
                  ))}
                </ul>
                {itemsOverflow > 0 && (
                  <div className={styles.confirmItemOverflow}>
                    {itemsOverflow} additional users are not shown
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          action.question && (
            <div className={styles.cardTitle}>{action.question}</div>
          )
        )}

        {structured ? (
          <div className={styles.confirmActions}>
            {options.map((option, optionIndex) => {
              const isSelected = effectiveSelected === option.value;
              const isFaded = isAnswered && !isSelected;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={confirmationButtonClass(
                    optionIndex === 0,
                    isSelected,
                    isFaded,
                    styles
                  )}
                  onClick={() => handleSelect(option)}
                  disabled={isAnswered || isModalOpen}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        ) : (
          <div className={styles.optionList}>
            {options.map((option, idx) => {
              const isSelected = effectiveSelected === option.value;
              const isFaded = isAnswered && !isSelected;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={optionButtonClass(isSelected, isFaded, styles)}
                  onClick={() => handleSelect(option)}
                  disabled={isAnswered || isModalOpen}
                >
                  {showLetters && (
                    <span className={styles.optionLetter}>{LETTERS[idx]}</span>
                  )}
                  <div className={styles.optionContent}>
                    <div className={styles.optionLabel}>{option.label}</div>
                    {option.description && (
                      <div className={styles.optionDescription}>
                        {option.description}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isModalOpen && (
        <MdrConsultationModal
          onClose={handleModalClose}
          onSubmissionComplete={handleSubmissionComplete}
        />
      )}
    </>
  );
}
